/**
 * CatererMealLeafPopup — full-screen banana-leaf style modal for one meal
 * (Tiffin / Lunch / Snacks / Dinner) on a single caterer card.
 *
 * Customer interactions inside the popup:
 *   • Editable guest count (auto-prefilled with current selection)
 *   • Veg / Non-Veg toggle (only when caterer offers non-veg for this meal)
 *   • Tick / untick individual menu items (defaults to all items selected)
 *   • Tap "Add to cart" → enables the meal on the parent card AND closes
 *   • Tap close (X) or backdrop → dismiss without saving
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, ForkKnife, Leaf, Plus } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const CatererMealLeafPopup = ({
  open,
  caterer,           // { id, name, business_name?, meals }
  mealKey,           // 'tiffin' | 'lunch' | 'snacks' | 'dinner'
  mealLabel,         // 'Tiffin' | etc.
  initialGuests,
  initialIsNonVeg,
  initialRemovedItems = [],
  pureVeg,
  onClose,
  onAdd,             // ({guests, isNonVeg, removedItems}) => void — parent enables meal + adds
}) => {
  const meal = caterer?.meals?.[mealKey] || {};
  const hasNV = !!meal.priceNV && !pureVeg;

  const [guests, setGuests] = useState(initialGuests || 0);
  const [isNV, setIsNV] = useState(!!initialIsNonVeg && hasNV);
  const [removed, setRemoved] = useState(new Set(initialRemovedItems));

  useEffect(() => {
    if (!open) return;
    setGuests(initialGuests || 0);
    setIsNV(!!initialIsNonVeg && hasNV);
    setRemoved(new Set(initialRemovedItems));
  }, [open, mealKey, caterer?.id, initialGuests, initialIsNonVeg, initialRemovedItems, hasNV]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const fullMenu = isNV && meal.menuNV ? meal.menuNV : (meal.menuVeg || []);
  const pricePerPlate = isNV && meal.priceNV ? meal.priceNV : (meal.priceVeg || 0);
  const activeCount = useMemo(() => fullMenu.filter((it) => !removed.has(it)).length, [fullMenu, removed]);
  const safeGuests = Math.max(0, parseInt(guests || 0, 10) || 0);
  const total = pricePerPlate * safeGuests;

  if (!open || !caterer) return null;

  const toggleItem = (item) => {
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item); else next.add(item);
      return next;
    });
  };

  const submit = () => {
    if (safeGuests < 1) return;
    if (activeCount < 1) return;
    onAdd({
      guests: safeGuests,
      isNonVeg: isNV,
      removedItems: Array.from(removed),
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[220] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="meal-leaf-popup"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg max-h-[92vh] sm:max-h-[88vh] rounded-t-2xl sm:rounded-xl overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[#E8E1D5]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-text-secondary truncate">
              {caterer.business_name || caterer.name}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <Leaf size={16} weight="fill" className="text-green-700 flex-shrink-0" />
              <span className="font-heading text-xl font-bold text-text-primary truncate" data-testid="meal-leaf-popup-title">
                {mealLabel} Menu
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-sand-100 hover:bg-sand-200 flex items-center justify-center text-text-primary flex-shrink-0"
            aria-label="Close"
            data-testid="meal-leaf-popup-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Banana leaf area */}
        <div className="flex-1 overflow-y-auto bg-gradient-to-b from-amber-50 to-amber-100 p-3">
          <div
            className="relative rounded-[40%/8%] mx-auto max-w-md min-h-[280px] sm:min-h-[320px] p-5 sm:p-7 shadow-inner"
            style={{
              background: 'linear-gradient(135deg, #1E5E29 0%, #2E7D38 35%, #3A8A45 60%, #2E7D38 90%)',
              boxShadow: 'inset 0 0 60px rgba(0,0,0,0.25)',
            }}
            data-testid="meal-leaf-popup-leaf"
          >
            {/* Faux leaf-vein highlight */}
            <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 h-[2px] bg-green-200/30" aria-hidden />
            <div className="relative grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {fullMenu.length === 0 && (
                <div className="col-span-3 text-center text-sand-50/80 text-sm py-8" data-testid="meal-leaf-popup-empty">
                  This caterer hasn&apos;t published a menu for {mealLabel} yet.
                </div>
              )}
              {fullMenu.map((item) => {
                const isOff = removed.has(item);
                return (
                  <button
                    type="button"
                    key={item}
                    onClick={() => toggleItem(item)}
                    className={`relative text-left p-2 rounded-lg border-2 transition-all backdrop-blur-[2px] ${isOff ? 'bg-black/20 border-white/20 opacity-60' : 'bg-white/90 border-white/60 hover:bg-white'}`}
                    data-testid={`meal-leaf-popup-item-${item.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    <div className="flex items-start gap-1.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isOff ? 'bg-gray-300' : 'bg-amber-100'}`}>
                        <ForkKnife size={13} className={isOff ? 'text-gray-500' : 'text-amber-700'} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-xs font-bold leading-tight ${isOff ? 'text-sand-50 line-through' : 'text-text-primary'}`}>
                          {item}
                        </div>
                        {!isOff && <div className="text-[9px] uppercase tracking-wider text-text-secondary mt-0.5">tap to remove</div>}
                        {isOff && <div className="text-[9px] uppercase tracking-wider text-sand-50/80 mt-0.5">tap to add back</div>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Veg / Non-Veg + Item count summary */}
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap" data-testid="meal-leaf-popup-controls">
            {hasNV ? (
              <div className="inline-flex border-2 border-[#E8E1D5] rounded overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsNV(false)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${!isNV ? 'bg-green-600 text-white' : 'bg-white text-text-secondary'}`}
                  data-testid="meal-leaf-popup-veg"
                >
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-sm mr-1 align-middle" />
                  Veg ₹{meal.priceVeg}
                </button>
                <button
                  type="button"
                  onClick={() => setIsNV(true)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider ${isNV ? 'bg-red-600 text-white' : 'bg-white text-text-secondary'}`}
                  data-testid="meal-leaf-popup-nv"
                >
                  <span className="inline-block w-2 h-2 bg-red-600 rounded-sm mr-1 align-middle" />
                  Non-Veg ₹{meal.priceNV}
                </button>
              </div>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-700 border border-green-300 font-bold rounded">
                <span className="inline-block w-2 h-2 bg-green-600 rounded-sm" /> Veg ₹{meal.priceVeg || 0}/plate
              </span>
            )}
            <div className="text-[11px] text-text-secondary">
              <span className="font-bold text-text-primary">{activeCount}</span> of {fullMenu.length} items selected
            </div>
          </div>
        </div>

        {/* Footer — guest count + add */}
        <div className="border-t border-[#E8E1D5] px-4 py-3 bg-sand-50/50 flex items-center gap-2 sm:gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider font-bold text-text-secondary">Guests</label>
            <Input
              type="number"
              min="1"
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              className="w-20 h-9 text-center rounded-none border-[#E8E1D5]"
              data-testid="meal-leaf-popup-guests"
            />
          </div>
          <div className="text-sm font-bold text-brand-primary" data-testid="meal-leaf-popup-total">
            ₹{total.toLocaleString('en-IN')}
            <span className="text-[10px] text-text-secondary font-normal ml-1">total</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-none border-[#E8E1D5] text-text-secondary hover:bg-sand-100"
              data-testid="meal-leaf-popup-close-btn"
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={safeGuests < 1 || activeCount < 1}
              className="rounded-none bg-brand-primary hover:bg-[#A84A38] text-sand-50 disabled:opacity-50"
              data-testid="meal-leaf-popup-add"
            >
              <Plus size={14} className="mr-1" /> Add to cart
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatererMealLeafPopup;
